import type { CSSProperties } from 'react'
import { Grid3x3, Link2, MousePointer, PaintBucket, Palette, UserPlus } from 'lucide-react'
import { ToolbarButton } from '@/components/ui/ToolbarButton'
import { ToolbarFooter } from '@/components/ui/ToolbarFooter'
import { ColorSwatchIndicator } from '@/components/toolbar/ColorSwatchIndicator'
import { HashIcon } from '@/components/toolbar/icons/HashIcon'
import { ColorPickerDialog } from '@/components/toolbar/dialogs/ColorPickerDialog'
import { HashDialog } from '@/components/toolbar/dialogs/HashDialog'
import type { SaveStatus } from '@/contexts/SaveStatusContext'
import type { DrawingState, HashAlignment } from '@/types'
import './formation-toolbar.css'

interface FormationToolbarProps {
	drawingState: DrawingState
	setDrawingState: (state: DrawingState) => void
	hashMode: boolean
	setHashMode: (mode: boolean) => void
	hashAlignment: HashAlignment
	showColorPicker: boolean
	setShowColorPicker: (show: boolean) => void
	showHashDialog: boolean
	setShowHashDialog: (show: boolean) => void
	autoMirror: boolean
	onAutoMirrorToggle: () => void
	// Props for dialogs rendered inside toolbar
	linemenAtDefault: boolean
	onHashAlignmentChange: (alignment: HashAlignment) => void
	saveStatus?: SaveStatus
	showPanel?: boolean
	onTogglePanel?: () => void
}

export function FormationToolbar({
									 drawingState,
									 setDrawingState,
									 hashMode,
									 setHashMode,
									 hashAlignment,
									 showColorPicker,
									 setShowColorPicker,
									 showHashDialog,
									 setShowHashDialog,
									 autoMirror,
									 onAutoMirrorToggle,
									 linemenAtDefault,
									 onHashAlignmentChange,
									 saveStatus,
									 showPanel,
									 onTogglePanel,
								 }: FormationToolbarProps) {
	const handleToolChange = (tool: DrawingState['tool']) => {
		if (tool === 'color') {
			setShowColorPicker(true)
			return
		}

		setDrawingState({ ...drawingState, tool })
	}

	const handleColorChange = (color: string) => {
		setDrawingState({ ...drawingState, color })
	}

	const handleHashModeToggle = () => {
		setHashMode(!hashMode)
	}

	return (
		<div className="editor-toolbar formation-toolbar">
			{/* Select Tool */}
			<ToolbarButton
				icon={MousePointer}
				tooltip="Select"
				isActive={drawingState.tool === 'select'}
				onClick={() => handleToolChange('select')}
			/>

			{/* Add Player Tool */}
			<ToolbarButton
				icon={UserPlus}
				tooltip="Add Player"
				isActive={drawingState.tool === 'addPlayer'}
				onClick={() => handleToolChange('addPlayer')}
			/>

			{/* Color Picker Tool with popup */}
			<div className="toolbar-dialog-anchor">
				<ToolbarButton
					icon={Palette}
					tooltip="Pick Color"
					isActive={showColorPicker}
					onClick={() => handleToolChange('color')}
				>
					<ColorSwatchIndicator color={drawingState.color} />
				</ToolbarButton>

				{showColorPicker && (
					<ColorPickerDialog
						currentColor={drawingState.color}
						onColorChange={handleColorChange}
						onClose={() => setShowColorPicker(false)}
						useRelativePosition={true}
					/>
				)}
			</div>

			{/* Fill Tool */}
			<ToolbarButton
				icon={PaintBucket}
				iconStyle={{ transform: 'scaleX(-1)' }}
				tooltip="Fill Color"
				isActive={drawingState.tool === 'fill'}
				onClick={() => handleToolChange('fill')}
			/>

			<div className="toolbar-separator" />

		{/* Auto-Mirror Toggle */}
			<ToolbarButton
				icon={Link2}
				tooltip={autoMirror ? 'Disable Auto-Mirror' : 'Enable Auto-Mirror'}
				isActive={autoMirror}
				onClick={onAutoMirrorToggle}
			>
				{autoMirror && <div className="toolbar-indicator-dot" style={{ '--dot-color': 'var(--color-blue)' } as CSSProperties} />}
			</ToolbarButton>

			{/* Hash Mode Toggle */}
			<ToolbarButton
				icon={Grid3x3}
				tooltip={hashMode ? 'Hash Mode: Hash-Specific' : 'Hash Mode: Same for All'}
				isActive={hashMode}
				onClick={handleHashModeToggle}
				disabled={autoMirror} // Disabled when auto-mirror is on (hash mode is required)
			>
				{hashMode && <div className="toolbar-indicator-dot" />}
			</ToolbarButton>

			{/* Hash Alignment Button with popup */}
			<div className="toolbar-dialog-anchor">
				<ToolbarButton
					icon={HashIcon}
					tooltip="Ball on Hash"
					isActive={showHashDialog}
					onClick={() => setShowHashDialog(!showHashDialog)}
				/>

				{showHashDialog && (
					<HashDialog
						currentAlignment={hashAlignment}
						linemenAtDefault={linemenAtDefault}
						onAlignmentChange={onHashAlignmentChange}
						onClose={() => setShowHashDialog(false)}
						useRelativePosition={true}
					/>
				)}
			</div>

			<ToolbarFooter
				showPanel={showPanel}
				onTogglePanel={onTogglePanel}
				saveStatus={saveStatus}
			/>
		</div>
	)
}
